import { createConnect } from './connect';
import { Event } from './event';
import { env } from './env';
import { copyFile } from './utils';

interface Config {
    // 应用ID， 在控制台应用详情查看
    appId: string;
    container?: string;
    onCloseModal?: () => Promise<void | boolean> | void | boolean;
    /**
     *  获取使用权益凭证
     */
    getUseRightCert?(info: {
        appId: string;
        abilityCode: string;
        workId: string;
    }): Promise<string | false | Error>;
    /**
     * 获取用户授权标识
     */
    authorize?(): Promise<string>;
}

type EditorModuleType =
    | 'panel.template'
    | 'panel.ajustment'
    | 'panel.mark'
    | 'panel.text'
    | 'panel.border'
    | 'panel.element'
    | 'panel.watermark'
    | 'panel.upload';

export function createImageEditor(config: Config) {
    const event = new Event();

    const tempCallbacks: Function[] = [];
    function cleanCallBack() {
        tempCallbacks.forEach((fn) => fn());
        tempCallbacks.length = 0;
    }

    const iframeManager = createConnect({
        container: config.container,
        appId: config.appId,
        async onClose() {
            if (config.onCloseModal) {
                const res = await config.onCloseModal();
                if (res === false) return false;
            }
            event.emit('close');
        },
        methods: {
            async emit(name: string, ...args: any) {
                event.emit(name, ...args);
            },
            onload() {
                // 兼容
                event.emit('load');
            },
            async getUseRightToken(info: { appId: string; abilityCode: string; workId: string }) {
                if (!config.getUseRightCert) return false;
                const token = await config.getUseRightCert(info);
                if (token instanceof Error) {
                    return {
                        message: token.message,
                    };
                }
                return token;
            },
            async getUserCode() {
                if (!config.authorize) return false;
                try {
                    return await config.authorize();
                } catch (error) {
                    close();
                }
            },
            async download(info: any) {
                event.emit('save', info);
            },
        },
    });

    function on(eventName: string, fn: (...args: any[]) => any) {
        event.on(eventName, fn);
    }

    function off(eventName: string, fn: (...args: any[]) => any) {
        event.off(eventName, fn);
    }

    type FileType = string | Blob;

    async function importImage(
        file: FileType | FileType[],
        options: { disableModules?: EditorModuleType[]; fileTypes?: string[] } = {},
    ) {
        cleanCallBack();
        iframeManager.update(env.IMAGE_EDITOR_URL, {
            version: config.authorize ? 'IE002' : 'IE001',
        });

        iframeManager.insert();

        event.once('load', async () => {
            const e = await iframeManager.getService();
            e.init({
                appId: config.appId,
                v: env.VERSION,
                version: config.authorize ? 'IE002' : 'IE001',
                fileTypes: options.fileTypes,
                disableModules: options.disableModules,
            });
            const service = await iframeManager.getService();
            const data = await Promise.all(
                (Array.isArray(file) ? file : [file]).map(async (f) =>
                    typeof f === 'string' ? f : await copyFile(f),
                ),
            );
            service.importFile(data);
        });

        return new Promise<{ files: Blob[]; workId: number; title: string; type: string } | false>(
            (resolve) => {
                tempCallbacks.push(event.once('save', (v) => resolve(v)));
                tempCallbacks.push(event.once('close', () => resolve(false)));
            },
        );
    }

    async function editImage(
        id: string,
        options: { disableModules?: EditorModuleType[]; fileTypes?: string[] } = {},
    ) {
        cleanCallBack();
        iframeManager.update(env.IMAGE_EDITOR_URL, {
            id,
            mode: 'user',
            version: config.authorize ? 'IE002' : 'IE001',
        });
        iframeManager.insert();

        event.once('load', async () => {
            const e = await iframeManager.getService();
            e.init({
                appId: config.appId,
                v: env.VERSION,
                version: config.authorize ? 'IE002' : 'IE001',
                fileTypes: options.fileTypes,
                disableModules: options.disableModules,
            });
        });
        return new Promise<{ files: Blob[]; workId: number; title: string; type: string } | false>(
            (resolve) => {
                tempCallbacks.push(event.once('save', (v) => resolve(v)));
                tempCallbacks.push(event.once('close', () => resolve(false)));
            },
        );
    }

    function close() {
        event.emit('close');
        iframeManager.remove();
    }

    function onSave(
        fn: (info: {
            workId: string;
            files: Blob[];
            title: string;
            type: string;
            indexes?: number[];
            sourceIndexes?: number[];
        }) => void,
    ) {
        event.add('save', fn);
    }

    function onClose(fn: () => any) {
        event.add('close', fn);
    }

    return {
        editImage,
        close,
        onSave,
        onClose,
        importImage,
        on,
        off,
    };
}
